import { Box, Button, Typography } from '@mui/material';
import React from 'react';

import { SectionLabel } from './SectionLabel';
import { LocationNodeData } from '../../../../@types';
import { SizeProps } from '../../../../@types/common.type';
import { LiveSearchBox, LiveSearchListItem } from '../../../../components/atoms/LiveSearchBox';
import { LocationSearchItem } from '../../../../components/molecules/LocationSearchItem';
import { LocationSearchSelect } from '../../../../components/organisms/LocationSearchSelect';

const hasEmptyParentTree = (node: LocationNodeData | undefined): boolean => {
  const raw = node?.parentTree;
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length === 0;
  } catch {
    return false;
  }
};

export interface LocationFieldProps {
  mainLocationNode?: LocationNodeData;
  subLocationNode?: LocationNodeData;
  subLocationOptions?: LocationNodeData[];
  locationText: string;
  searchError: boolean;
  disabled?: boolean;
  onSearch: (name: string) => Promise<LocationNodeData[]>;
  onSelect: (node: LocationNodeData) => Promise<void> | void;
  onSelectSubLocation: (node: LocationNodeData) => void;
  onClear: () => void;
  onDismissOptions: () => void;
  onRequestEdit?: () => void;
  canEdit?: boolean;
  error?: string;
}

export const LocationField: React.FC<LocationFieldProps> = ({
  mainLocationNode,
  subLocationNode,
  subLocationOptions,
  locationText,
  searchError,
  disabled,
  onSearch,
  onSelect,
  onSelectSubLocation,
  onClear,
  onDismissOptions,
  onRequestEdit,
  canEdit,
  error,
}) => (
  <SectionLabel label="Location" required>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1 }}>
        {subLocationOptions ? (
          <LocationSearchSelect
            title="Choose sub-location"
            options={subLocationOptions}
            value={subLocationNode}
            onClose={onDismissOptions}
            onChange={onSelectSubLocation}
          />
        ) : (
          <LiveSearchBox
            title="Search location"
            hideInlineLabel
            isRequired
            timeOffset={400}
            freeSolo={false}
            multiline={false}
            value={locationText}
            disabled={disabled}
            onClearValue={onClear}
            renderItem={(props, option) => (
              <LiveSearchListItem key={option.id || option.name} {...props}>
                <LocationSearchItem data={option} />
              </LiveSearchListItem>
            )}
            onChange={(node: LocationNodeData) => onSelect(node)}
            onApiInvoke={(name) => onSearch(name)}
            isError={searchError || !!error}
            size={SizeProps.Medium}
          />
        )}
        {mainLocationNode?.name && !subLocationOptions && (
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
            {mainLocationNode.name}
            {subLocationNode?.name ? ` / ${subLocationNode.name}` : ''}
            {hasEmptyParentTree(mainLocationNode) && (
              <Box component="span" sx={{ ml: 1, color: 'warning.main', fontWeight: 500 }}>
                — No Client or Contract set for Job Location
              </Box>
            )}
          </Typography>
        )}
      </Box>
      {onRequestEdit && (
        <Button
          variant="outlined"
          size="small"
          disabled={!mainLocationNode?.name || !canEdit}
          onClick={onRequestEdit}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Edit location
        </Button>
      )}
    </Box>
  </SectionLabel>
);
